import { useMemo } from 'react';
import { Grid } from '@gonfalon/dataviz-experimental/components/Grid';
import { LineChart as DatavizLineChart } from '@gonfalon/dataviz-experimental/components/LineChart';
import { Series } from '@gonfalon/dataviz-experimental/components/Series';
import { Tooltip } from '@gonfalon/dataviz-experimental/components/Tooltip';
import { XAxis } from '@gonfalon/dataviz-experimental/components/XAxis';
import { YAxis } from '@gonfalon/dataviz-experimental/components/YAxis';
import type { ChartDataPoint } from '@gonfalon/dataviz-experimental/types/ChartDataPoint';
import type { DataType } from '@gonfalon/dataviz-experimental/utils/formatters';
import nullthrows from 'nullthrows';

import { useIsAnalyzingResultsData } from '#components/experimentation/common/hooks/useResultsEmptyStateMessage';
import { useExperimentIterationContext } from '#components/experimentation/common/providers/ExperimentIterationContextProvider';

import { getExperimentExposuresChartEmptyState } from './getExperimentExposuresChartEmptyState';
import { useExperimentTrafficChartData } from './hooks/useExperimentTrafficChartData';

import styles from './ExperimentDetailsTrafficChart.module.css';

export type ExperimentDetailsTrafficChartInnerProps = {
  /** Flattened chart data: array of { timestamp, [key]: value }. */
  chartData: ChartDataPoint[] | null;
  /** Map from series key (e.g. treatment key) to color hex. */
  keyToColorMap: Record<string, string>;
  /** Y-axis value type for formatting. */
  yAxisDataType?: DataType;
  isLoading?: boolean;
  /** Chart height in pixels. */
  height?: number;
  emptyStateTitle?: string;
  emptyStateSubtitle?: string;
};

/**
 * Presentational multi-series line chart for traffic-shaped time series data,
 * using the same dataviz-experimental pattern as TrendsLineChartVisx (no zoom/series highlight).
 */
export function ExperimentDetailsTrafficChartInner({
  chartData,
  keyToColorMap,
  yAxisDataType = 'number',
  isLoading = false,
  height = 120,
  emptyStateTitle,
  emptyStateSubtitle = '',
}: ExperimentDetailsTrafficChartInnerProps) {
  const seriesConfigs = useMemo(() => {
    if (!chartData || chartData.length === 0) {
      return [];
    }

    const allKeys = new Set<string>();
    chartData.forEach((dataPoint) => {
      Object.keys(dataPoint).forEach((key) => {
        if (key === 'timestamp') {
          return;
        }
        allKeys.add(key);
      });
    });

    const keys = Array.from(allKeys).sort();

    return keys.map((dataKey, index) => {
      const color = keyToColorMap[dataKey] ?? Object.values(keyToColorMap)[index] ?? '#000000';
      return {
        seriesKey: `series-${dataKey}`,
        dataKey,
        name: dataKey,
        color,
        strokeWidth: 2,
      };
    });
  }, [chartData, keyToColorMap]);

  const xAxisLabelFormatter = useMemo(
    () => (value: number) =>
      new Date(value).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      }),
    [],
  );

  return (
    <div className={styles.chart}>
      <DatavizLineChart
        data={chartData ?? []}
        height={height}
        margin={{ top: 20, right: 20, bottom: 40 }}
        isLoading={isLoading}
        emptyStateTitle={emptyStateTitle}
        emptyStateSubtitle={emptyStateSubtitle}
      >
        <Grid />
        <XAxis dataKey="timestamp" dataType="time" />
        <YAxis dataType={yAxisDataType} numTicks={2} />
        {seriesConfigs.map((config) => (
          <Series
            key={config.seriesKey}
            seriesKey={config.seriesKey}
            name={config.name}
            dataKey={config.dataKey}
            color={config.color}
            strokeWidth={config.strokeWidth}
          />
        ))}
        <Tooltip enabled xAxisLabelFormatter={xAxisLabelFormatter} yAxisDataType={yAxisDataType} />
      </DatavizLineChart>
    </div>
  );
}

export type ExperimentDetailsTrafficChartProps = {
  height?: number;
};

/**
 * Experiment iteration traffic chart: reads iteration context, loads traffic series via
 * {@link useExperimentTrafficChartData}, and renders {@link ExperimentDetailsTrafficChartInner}.
 */
export function ExperimentDetailsTrafficChart({ height = 120 }: ExperimentDetailsTrafficChartProps) {
  const { iteration, experiment, flag } = useExperimentIterationContext();
  const isAnalyzingData = useIsAnalyzingResultsData();

  const { chartData, isLoading, keyToColorMap } = useExperimentTrafficChartData({
    experiment,
    iterationId: nullthrows(iteration._id, 'missing iteration id'),
    iteration,
    flagVariations: flag?.variations,
  });

  const { emptyStateTitle, emptyStateSubtitle } = getExperimentExposuresChartEmptyState(
    iteration.status,
    isAnalyzingData,
  );

  return (
    <ExperimentDetailsTrafficChartInner
      chartData={chartData}
      keyToColorMap={keyToColorMap}
      yAxisDataType="number"
      isLoading={isLoading}
      height={height}
      emptyStateTitle={emptyStateTitle}
      emptyStateSubtitle={emptyStateSubtitle}
    />
  );
}
